import { emitToast } from "./toastEmitter";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "/api";

interface FetchOptions extends RequestInit {
  /** If true, don't show a global toast on error (for forms with inline error display). */
  suppressToast?: boolean;
}

/**
 * Authenticated fetch wrapper. Adds Bearer token and handles JSON.
 * Automatically emits a toast on error unless suppressToast is set.
 */
export async function apiFetch<T = unknown>(
  path: string,
  token: string,
  options: FetchOptions = {}
): Promise<T> {
  const { suppressToast, ...fetchOptions } = options;
  const res = await fetch(`${API_URL}${path}`, {
    ...fetchOptions,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...fetchOptions.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new ApiError(res.status, body.error ?? `API error: ${res.status}`);
    if (!suppressToast) emitToast(error.message);
    throw error;
  }

  return res.json();
}

export async function apiUpload<T = unknown>(
  path: string,
  token: string,
  file: File,
  options?: { suppressToast?: boolean },
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const error = new ApiError(res.status, body.error ?? `Upload failed: ${res.status}`);
    if (!options?.suppressToast) emitToast(error.message);
    throw error;
  }
  return res.json();
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}
